import { useState } from 'react';
import { useGlossary } from '../../contexts/GlossaryContext';
import type { SevaGig } from '../../lib/types';
import { VEDIC_DIMENSIONS } from '../../lib/vedicDimensions';
import { SAMPRADAYA_OPTIONS } from '../../lib/sampradaya';

interface Props {
  gig: SevaGig | null;
  mode?: 'intro' | 'book';
  providerAlias?: string;
  onClose: () => void;
  onBook: (gig: SevaGig, brief: string, locationOptIn: boolean, introRequested: boolean) => void;
  isBooking: boolean;
  error?: string | null;
}

export default function ProviderModal({ gig, mode = 'book', providerAlias, onClose, onBook, isBooking, error }: Props) {
  const { t } = useGlossary();
  const [brief, setBrief] = useState('');
  const [locationOptIn, setLocationOptIn] = useState(false);

  if (!gig) return null;

  const isIntro = mode === 'intro';
  const dim = VEDIC_DIMENSIONS.find((d) => d.id === gig.dimension);
  const sampradaya = gig.sampradaya ? SAMPRADAYA_OPTIONS.find((s) => s.id === gig.sampradaya) : null;
  const isHomeCapable = gig.mode === 'home' || gig.mode === 'either';

  const handleBook = (e: React.FormEvent) => {
    e.preventDefault();
    onBook(gig, brief, locationOptIn, isIntro);
  };

  return (
    <div className="seva-modal-backdrop" role="dialog" aria-modal="true" aria-label={gig.title}>
      <div className={`seva-modal${isIntro ? ' seva-modal--intro' : ''}`}>
        <header className="seva-modal__header">
          <span className="seva-modal__dim-icon">{dim?.icon ?? '◎'}</span>
          <div>
            <h2 className="seva-modal__title">
              {isIntro ? `📞 ${t('seva.gigs.meet_first.form.title')}` : gig.title}
            </h2>
            <span className="seva-modal__dim-label">{dim?.label ?? gig.dimension}</span>
          </div>
          <button className="seva-modal__close" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </header>

        <div className="seva-modal__body">
          {isIntro && (
            <div className="seva-modal__intro-note">
              <p>{t('seva.gigs.meet_first.note')}</p>
            </div>
          )}

          <section className="seva-modal__provider-info">
            <h3 className="seva-modal__section-title">{t('seva.gigs.provider.label')}</h3>
            <p className="seva-modal__alias">{providerAlias ?? t('seva.gigs.provider.anonymous')}</p>
            {sampradaya && (
              <p className="seva-modal__sampradaya">
                {sampradaya.icon} {t(sampradaya.glossaryKey)}
                <span className="seva-modal__sampradaya-label"> — {t('seva.gigs.sampradaya.label')}</span>
              </p>
            )}
            {gig.portfolio_url && (
              <a href={gig.portfolio_url} target="_blank" rel="noopener noreferrer" className="seva-modal__portfolio-link">
                {t('seva.gigs.provider.portfolio')} ↗
              </a>
            )}
          </section>

          <section className="seva-modal__details">
            <p className="seva-modal__desc">{gig.description}</p>
            <dl className="seva-modal__meta">
              <dt>{t('seva.gigs.rate.label')}</dt>
              <dd>₹{gig.rate_inr.toLocaleString('en-IN')}</dd>
              <dt>{t('seva.gigs.mode.label')}</dt>
              <dd>{gig.mode === 'home'
                    ? `🏠 ${t('seva.gigs.mode.home')}`
                    : gig.mode === 'remote'
                    ? `💻 ${t('seva.gigs.mode.remote')}`
                    : `🔄 ${t('seva.gigs.mode.either')}`}
              </dd>
            </dl>
          </section>

          {/* India Stack verification status */}
          <section className="seva-modal__india-stack">
            <h3 className="seva-modal__section-title">{t('seva.gigs.india.stack.label')}</h3>
            <ul className="seva-modal__kyc-list">
              <li className={gig.india_stack?.aadhaar_verified ? 'seva-kyc--verified' : 'seva-kyc--pending'}>
                🪪 {t('seva.gigs.india.stack.aadhaar')}
                {gig.india_stack?.aadhaar_verified ? ' ✓' : ` — ${t('seva.gigs.india.stack.pending')}`}
              </li>
              <li className={gig.india_stack?.digi_locker_linked ? 'seva-kyc--verified' : 'seva-kyc--pending'}>
                📁 {t('seva.gigs.india.stack.digilocker')}
                {gig.india_stack?.digi_locker_linked ? ' ✓' : ` — ${t('seva.gigs.india.stack.pending')}`}
              </li>
            </ul>
          </section>

          {gig.requires_raja_per_booking && !isIntro && (
            <div className="seva-modal__raja-note">
              <span>⚖️</span>
              <p>{t('seva.gigs.raja.approval.note')}</p>
            </div>
          )}

          {/* Booking / intro-request form */}
          <form className="seva-modal__book-form" onSubmit={handleBook}>
            <h3 className="seva-modal__section-title">
              {isIntro ? t('seva.gigs.meet_first.form.brief_title') : t('seva.gigs.book.form.title')}
            </h3>
            <label className="seva-modal__form-label" htmlFor="seva-book-brief">
              {t('seva.gigs.book.brief.label')}
            </label>
            <textarea
              id="seva-book-brief"
              className="seva-modal__textarea"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={3}
              placeholder={isIntro
                ? t('seva.gigs.meet_first.brief.placeholder')
                : t('seva.gigs.book.brief.placeholder')}
            />

            {isHomeCapable && !isIntro && (
              <label className="seva-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={locationOptIn}
                  onChange={(e) => setLocationOptIn(e.target.checked)}
                />
                🏠 {t('seva.gigs.book.location.optin')}
              </label>
            )}

            {error && <p className="seva-modal__error" role="alert">{error}</p>}

            <div className="seva-modal__form-actions">
              <button type="button" className="seva-btn seva-btn--secondary" onClick={onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="seva-btn seva-btn--primary" disabled={isBooking}>
                {isBooking
                  ? (isIntro ? t('seva.gigs.meet_first.submitting') : t('seva.gigs.book.submitting'))
                  : (isIntro ? `📞 ${t('seva.gigs.meet_first.confirm')}` : t('seva.gigs.book.confirm'))}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
